import { InfiniteData } from '@tanstack/react-query';

import OrganizationRepository, {
  OrganizationsParams
} from '@/repositories/members/OrganizationRepository';
import type { Organization } from '@/types/organization';
import type PaginatedResponse from '@/types/paginatedResponse';
import type RepositoryResponse from '@/types/repositoryResponse';

import { ONBOARDING_QUERY_KEY_PREFIX } from '../pages/Onboarding/components/Stepper';

import { useAPIPaginated } from './useAPI';

const defaultOrganizationSearchParams = {
  search: '',
  referrer_code: ''
};

export const useOrganizationsPaginated = ({
  params = { ...defaultOrganizationSearchParams },
  enabled = true
}: {
  params?: Omit<OrganizationsParams, 'page'>;
  enabled?: boolean;
} = {}) => {
  const organizationRepository = OrganizationRepository();

  return useAPIPaginated({
    queryKey: [
      ONBOARDING_QUERY_KEY_PREFIX,
      'organizations-paginated',
      params.search,
      params.referrer_code
    ],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const paramsWithPage = {
        ...defaultOrganizationSearchParams,
        ...params,
        page: pageParam.toString()
      };
      return await organizationRepository.getOrganizations(paramsWithPage);
    },
    select: (data: InfiniteData<RepositoryResponse<PaginatedResponse<Organization[]>>>) => {
      const organizationsFormattedForSelect = data.pages.flatMap((page) => {
        if (!page.data?.results) {
          return [];
        }
        return page.data.results.map((organization) => ({
          label: organization.name,
          value: organization.id
        }));
      });

      return {
        organizations: organizationsFormattedForSelect,
        pages: data.pages,
        pageParams: data.pageParams
      };
    },
    enabled
  });
};
